import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import dayjs from "dayjs";

import { useOnMount } from "~/common/hooks/useOnMount";
import {
  QUERY_TYPE,
  type QueryType,
  type SearchByData,
  validateSearchQuery,
} from "~/features/orders/common/queryTypes";
import {
  OrderbookSearchInput,
  ORDERBOOK_LABEL_KEYS,
} from "~/features/orderbook/components/OrderbookSearchBox/OrderbookSearchInput";
import { Box, Grid } from "~/common/mui";
import { ChipSelect } from "~/common/mui/Chip/ChipSelect";
import {
  AdvancedDateRangePicker,
  type AdvancedDateRangeValue,
} from "~/common/components/AdvancedDateRangePicker/AdvancedDateRangePicker";

import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import {
  getInitialAccounts,
  MultiAccountSelect,
} from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSelect";
import { type SingleAccountData } from "~/features/orders/common/DownloadOrderHistoryService/types";
import { useMultiAccountSearchData } from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";

import { useTranslation } from "react-i18next";
import {
  deserializeDateRange,
  useFormPersistence,
} from "~/features/orders/orderList/components/OrderListSearchBox/searchFormPersistenceUtils";

const ORDERBOOK_STORAGE_KEY = "orderbookSearchData";

interface Props {
  isLoading?: boolean;
  onSubmit: (data: OrderbookSearchBoxData) => void;
  disabled?: boolean;
  children?: ReactNode;
}

export interface OrderbookSearchBoxHandle {
  reset: () => void;
}

export interface OrderbookSearchBoxData {
  query: string;
  queryType: QueryType;
  dateFrom?: Date;
  dateTo?: Date;
  lineStatus?: string;
  accounts?: SingleAccountData[];
  confirmedLines: string[];
}

export interface OrderbookSearchFormData {
  dateRange: AdvancedDateRangeValue;
  lineStatusOption: string;
  searchBy: SearchByData;
  confirmedLines: string[];
  accounts: string[];
}

const INITIAL_SEARCH_FORM_DATA: OrderbookSearchFormData = {
  dateRange: {
    shortcut: "last30Days",
    dates: [dayjs().subtract(30, "day"), dayjs()],
  },
  lineStatusOption: "",
  searchBy: {
    query: "",
    type: QUERY_TYPE.PURCHASE_NO,
  },
  accounts: getInitialAccounts(),
  confirmedLines: [],
};

export const getDatesFromForm = (dateRange: AdvancedDateRangeValue) => ({
  dateFrom: dateRange.dates[0]?.toDate(),
  dateTo: dateRange.dates[1]?.toDate(),
});

const OrderbookSearchBox = forwardRef<OrderbookSearchBoxHandle, Props>(
  ({ isLoading: _isLoading, onSubmit, disabled: _disabled, children }, ref) => {
    const { t } = useTranslation();
    const multiAccountData = useMultiAccountSearchData();

    const formHooks = useForm<OrderbookSearchFormData>({
      defaultValues: INITIAL_SEARCH_FORM_DATA,
      mode: "onTouched",
    });

    useFormPersistence<OrderbookSearchFormData>(
      ORDERBOOK_STORAGE_KEY,
      formHooks.reset,
      formHooks.getValues,
      {
        version: 1,
        deserialize: (data) => ({
          ...(data as unknown as OrderbookSearchFormData),
          dateRange: deserializeDateRange(
            data.dateRange as Record<string, unknown>,
          ),
        }),
      },
    );

    const getValues = formHooks.getValues;

    const confirmedDeliveryChips = useMemo(
      () =>
        OrderbookLineConfirmedDeliveryEntries.filter(
          (e) => e.value !== OrderbookConfirmedDeliveryStatus.ALL,
        ),
      [],
    );

    const hasInitiallySubmitted = useRef(false);

    const onSubmitCb = useCallback(() => {
      const formData = getValues() as OrderbookSearchFormData;
      const {
        lineStatusOption,
        searchBy,
        accounts: formAccounts,
        confirmedLines,
      } = formData;

      let accounts:
        | {
            accountNumber: string;
            endCustomer: string;
            salesOrg: string;
          }[]
        | undefined = undefined;
      if (formAccounts) {
        accounts = formAccounts
          .map((str) => str.split("-"))
          .map((parts) => ({
            accountNumber: parts[0],
            endCustomer: parts[1],
            salesOrg: parts[2],
          }));
      }

      const requestPayload: OrderbookSearchBoxData = {
        query: searchBy.query,
        queryType: searchBy.type,
        lineStatus: lineStatusOption || undefined,
        ...getDatesFromForm(formData.dateRange),
        accounts,
        confirmedLines,
      };

      onSubmit(requestPayload);
    }, [getValues]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          localStorage.removeItem(ORDERBOOK_STORAGE_KEY);
          formHooks.reset(INITIAL_SEARCH_FORM_DATA);
          onSubmitCb();
        },
      }),
      [formHooks, onSubmitCb],
    );

    useOnMount(() => {
      onSubmitCb();
      hasInitiallySubmitted.current = true;
    });

    useEffect(() => {
      const subscription = formHooks.watch((_value, { name, type }) => {
        if (
          hasInitiallySubmitted.current &&
          name &&
          !name.startsWith("searchBy") &&
          type === "change"
        ) {
          onSubmitCb();
        }
      });
      return () => subscription.unsubscribe();
    }, [formHooks, onSubmitCb]);

    return (
      <FormProvider {...formHooks}>
        <form onSubmit={formHooks.handleSubmit(onSubmitCb)} role="searchbox">
          <Grid container gap={"var(--gutter)"} columns={12} rowGap={3}>
            <Grid size={6}>
              <Controller
                render={({
                  field: { value, onChange, onBlur, ref },
                  fieldState: { error },
                }) => (
                  <OrderbookSearchInput
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    onBlur={onBlur}
                    ref={ref}
                  />
                )}
                name={"searchBy"}
                defaultValue={INITIAL_SEARCH_FORM_DATA.searchBy}
                control={formHooks.control}
                rules={{
                  validate: validateSearchQuery(t, ORDERBOOK_LABEL_KEYS),
                }}
              />
            </Grid>
            <Grid size={3}>
              {isMultiAccountOrderSearchEnabled() && (
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <MultiAccountSelect values={value} onChange={onChange} />
                  )}
                  name={"accounts"}
                  defaultValue={INITIAL_SEARCH_FORM_DATA.accounts}
                  control={formHooks.control}
                />
              )}
            </Grid>

            <Grid size={3}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <AdvancedDateRangePicker
                    value={value}
                    onChange={onChange}
                    sx={{ width: "100%" }}
                  />
                )}
                name={"dateRange"}
                control={formHooks.control}
              />
            </Grid>

            <Grid
              size={12}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                {multiAccountData.showUnconfirmedLinesFilter && (
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <ChipSelect
                        multiple
                        value={value}
                        onChange={(_, v) => onChange(v)}
                        label={t("orderList.orderConfirmedDeliveryStatus")}
                      >
                        {confirmedDeliveryChips.map((entry) => (
                          <ChipSelect.Option
                            key={entry.value}
                            value={entry.value}
                          >
                            {t(entry.label as string)}
                          </ChipSelect.Option>
                        ))}
                      </ChipSelect>
                    )}
                    name={"confirmedLines"}
                    control={formHooks.control}
                  />
                )}
              </Box>
              <Box>{children}</Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    );
  },
);

OrderbookSearchBox.displayName = "OrderbookSearchBox";

export default OrderbookSearchBox;

export const enum OrderbookConfirmedDeliveryStatus {
  ALL = "all",
  CONFIRMED = "confirmed",
  UNCONFIRMED = "unconfirmed",
}
export const OrderbookLineConfirmedDeliveryEntries = [
  {
    label: "page.abmonitor.filter.linestatus.all",
    value: OrderbookConfirmedDeliveryStatus.ALL,
  },
  {
    label: "page.abmonitor.filter.linestatus.confirmed",
    value: OrderbookConfirmedDeliveryStatus.CONFIRMED,
  },
  {
    label: "page.abmonitor.filter.linestatus.unconfirmed",
    value: OrderbookConfirmedDeliveryStatus.UNCONFIRMED,
  },
];
